import type { Candle, Instrument, InstrumentId } from "@trader/shared/MarketData"
import { formatDecimal } from "@trader/shared/Money"
import { BigDecimal } from "effect"
import {
  createSeriesMarkers,
  type IChartApi,
  type IPriceLine,
  type ISeriesApi,
  type ISeriesMarkersPluginApi,
  type ISeriesPrimitive,
  LineStyle,
  type SeriesMarker,
  type Time,
  type UTCTimestamp
} from "lightweight-charts"
import { toast } from "../../ui/toasts.ts"
import { boxLabels } from "../chart/boxLabels.ts"
import { MarkerTips } from "../chart/markerTips.ts"
import { type TradeBox, TradeBoxesPrimitive } from "../chart/tradeBoxPrimitive.ts"
import { type AccountState, fillsOn } from "./accountStore.ts"
import {
  type ChartLine,
  chartLines,
  closedTrades,
  type LineRequest,
  moveRequest,
  orderById,
  orderPnlAt,
  orderProtectRequest,
  orderStartingPrice,
  pnlAt,
  positionBoxes,
  positionBoxLabels,
  positionFor,
  positionOpenedAt,
  protectRequest,
  shiftRequest,
  snapPrice,
  startingPrice
} from "./chartOrders.ts"
import { formatSignedMoney, formatTime } from "./format.ts"
import { type TicketInstrument, ticketInstrument } from "./orderTicket.ts"
import { closePosition, sendLineRequest } from "./positionActions.ts"
import { messageFor, tradingApi } from "./tradingApi.ts"
import { accountStore } from "./tradingData.ts"

// The followed paper account on one chart (docs/design/0005, TradingLayer; docs/design/0013):
// a line per working order, a line at each open position's average price, its stop-loss and
// take-profit, and an arrow per fill. Each line has a label on the chart: drag it to move the
// order (Discard / Confirm), × to cancel or close, and SL / TP on a position to add a stop-loss
// or a take-profit. Plain TypeScript; ChartPane creates and disposes it. The labels are HTML over
// the chart, placed again whenever the chart redraws (a series primitive tells us when).

type Decimal = BigDecimal.BigDecimal

const COLORS: Readonly<Record<ChartLine["tone"], string>> = {
  buy: "#2962ff",
  sell: "#f23645",
  long: "#26a69a",
  short: "#ef5350",
  loss: "#f23645",
  profit: "#26a69a"
}
const BUY = COLORS.buy
const SELL = COLORS.sell

const toNumber = (value: Decimal): number => BigDecimal.toNumberUnsafe(value)

/** Bars a position's box reaches past the current bar (within the chart's right offset of 6). */
const BOX_AHEAD = 5

/** A label's height and the gap between labels side by side (px; matches .trade-line). */
const LABEL_HEIGHT = 24
const LABEL_GAP = 8

/** A line being moved: dragged and waiting for Confirm, or a new stop-loss / take-profit. */
interface Draft {
  readonly line: ChartLine
  price: Decimal
  /** What Confirm sends for a price. */
  readonly request: (price: Decimal) => LineRequest | null
  sending: boolean
  error: string | null
  /** Sent and accepted: shown at its new price until the account update arrives. */
  sentAt: number | null
  /** Moved along with another line (a Shift-drag's brackets): that line's key; it has no buttons of its own. */
  readonly ledBy: string | null
}

interface Handle {
  readonly root: HTMLDivElement
  readonly label: HTMLButtonElement
  readonly actions: HTMLSpanElement
}

/** The bar a time falls in (the last bar opening at or before it), for marker placement. */
const barTimeFor = (bars: ReadonlyArray<Candle>, time: number): number | undefined => {
  let low = 0
  let high = bars.length
  while (low < high) {
    const mid = (low + high) >>> 1
    if (bars[mid]!.time <= time) low = mid + 1
    else high = mid
  }
  return low === 0 ? undefined : bars[low - 1]!.time
}

const button = (text: string, className: string, title: string, onClick: () => void): HTMLButtonElement => {
  const element = document.createElement("button")
  element.type = "button"
  element.className = className
  element.textContent = text
  element.title = title
  element.addEventListener("pointerdown", (event) => event.stopPropagation())
  element.addEventListener("click", (event) => {
    event.stopPropagation()
    onClick()
  })
  return element
}

export class TradingLayer {
  private readonly candles: ISeriesApi<"Candlestick">
  private readonly markers: ISeriesMarkersPluginApi<Time>
  /** A fill mark's side, size and price while the pointer is on it. */
  private readonly tips: MarkerTips
  /** Each protected position's long / short box (entry to its stop-loss and take-profit). */
  private readonly boxes = new TradeBoxesPrimitive()
  private readonly overlay: HTMLDivElement
  private readonly primitive: ISeriesPrimitive<Time>
  private readonly lines = new Map<string, IPriceLine>()
  private readonly handles = new Map<string, Handle>()
  /** What each handle shows now (its listeners read it). */
  private readonly shown = new Map<string, ChartLine>()
  private readonly drafts = new Map<string, Draft>()
  private instrumentId: InstrumentId | null = null
  private spec: TicketInstrument | null = null
  private bars: ReadonlyArray<Candle> = []
  private lineKey = ""
  private markerKey = ""
  private placeFrame = 0
  private readonly stop: () => void

  constructor(chart: IChartApi, candles: ISeriesApi<"Candlestick">, container: HTMLElement) {
    this.candles = candles
    this.markers = createSeriesMarkers(candles, [])
    this.tips = new MarkerTips(chart)
    candles.attachPrimitive(this.boxes)
    this.overlay = document.createElement("div")
    this.overlay.className = "trade-lines"
    container.appendChild(this.overlay)
    // Called on every redraw (scroll, zoom, resize, new bars): the labels follow their prices.
    this.primitive = { updateAllViews: () => this.schedulePlace() }
    candles.attachPrimitive(this.primitive)
    this.stop = accountStore.subscribe(() => this.draw())
  }

  setInstrument(instrumentId: InstrumentId): void {
    if (instrumentId === this.instrumentId) return
    this.instrumentId = instrumentId
    this.drafts.clear()
    this.draw()
  }

  /** The instrument's tick size and currency: drags snap to its ticks; P&L shows in its currency. */
  setInstrumentDetails(instrument: Instrument): void {
    if (instrument.id !== this.instrumentId) return
    this.spec = ticketInstrument(instrument)
    this.draw()
  }

  /** The chart's bars, to place fill markers on the bar they happened in. */
  setBars(bars: ReadonlyArray<Candle>): void {
    this.bars = bars
    this.draw()
  }

  dispose(): void {
    this.stop()
    cancelAnimationFrame(this.placeFrame)
    this.candles.detachPrimitive(this.primitive)
    this.candles.detachPrimitive(this.boxes)
    for (const line of this.lines.values()) this.candles.removePriceLine(line)
    this.lines.clear()
    this.overlay.remove()
    this.markers.detach()
    this.tips.dispose()
  }

  private draw(): void {
    // A drawing problem must never break the store's other listeners or the chart.
    try {
      const state = accountStore.getState()
      this.drawLines(state)
      this.drawMarkers(state)
    } catch (error) {
      console.error("TradingLayer: couldn't draw the account on the chart", error)
    }
  }

  // --- lines and their labels ------------------------------------------------------------------

  /** The account's lines with any drafts applied (moved prices, new SL / TP lines). */
  private currentLines(state: AccountState | null): Array<ChartLine> {
    const lines = [...chartLines(state, this.instrumentId)]
    const keys = new Set(lines.map((line) => line.key))
    for (const [key, draft] of this.drafts) {
      const line = lines.find((candidate) => candidate.key === key)
      // A sent draft is done once the account shows its price, or after a moment regardless.
      if (draft.sentAt !== null && (line === undefined || BigDecimal.equals(line.price, draft.price) || Date.now() - draft.sentAt > 3000)) {
        this.drafts.delete(key)
        continue
      }
      // The order filled or was cancelled while it (or its own SL / TP) was being moved.
      const gone = draft.line.orderId !== null || (draft.line.parentOrderId !== null && orderById(state, draft.line.parentOrderId) === undefined)
      if (line === undefined && gone) {
        this.drafts.delete(key)
        continue
      }
      if (!keys.has(key)) lines.push({ ...draft.line, price: draft.price })
    }
    return lines.map((line) => {
      const draft = this.drafts.get(line.key)
      return draft === undefined ? line : { ...line, price: draft.price }
    })
  }

  private drawLines(state: AccountState | null): void {
    const lines = this.currentLines(state)
    this.drawBoxes(state, lines)
    const key = lines.map((line) => `${line.key}|${formatDecimal(line.price)}|${line.label}|${line.closable}`).join(";") +
      `#${[...this.drafts.values()].map((d) => `${d.line.key}:${d.sending}:${d.error}`).join(",")}` +
      `#${this.positionsKey(state)}`
    if (key === this.lineKey) return
    this.lineKey = key

    const wanted = new Set(lines.map((line) => line.key))
    for (const [lineKey, line] of this.lines) {
      if (wanted.has(lineKey)) continue
      this.candles.removePriceLine(line)
      this.lines.delete(lineKey)
    }
    for (const [lineKey, handle] of this.handles) {
      if (wanted.has(lineKey)) continue
      handle.root.remove()
      this.handles.delete(lineKey)
      this.shown.delete(lineKey)
    }
    for (const line of lines) {
      const draft = this.drafts.get(line.key)
      const options = {
        price: toNumber(line.price),
        color: COLORS[line.tone],
        lineWidth: 1 as const,
        lineStyle: draft !== undefined ? LineStyle.Dotted : line.kind === "position" ? LineStyle.Solid : LineStyle.Dashed,
        axisLabelVisible: true,
        title: ""
      }
      const existing = this.lines.get(line.key)
      if (existing) existing.applyOptions(options)
      else this.lines.set(line.key, this.candles.createPriceLine(options))
      this.shown.set(line.key, line)
      this.renderHandle(line, draft, state)
    }
    this.place()
  }

  /**
   * TradingView's long / short position tool on each protected position (docs/design/0016): from
   * the bar it opened on to a few bars past the current one, green from the average price to the
   * take-profit and red to the stop-loss, with the running profit or loss and a dashed line from
   * the entry to the current price. A finished trade keeps its box, from its entry bar to its exit
   * bar, with its closed P&L (docs/design/0016).
   */
  private drawBoxes(state: AccountState | null, lines: ReadonlyArray<ChartLine>): void {
    const first = this.bars[0]
    const last = this.bars.at(-1)
    if (state === null || first === undefined || last === undefined) {
      this.boxes.setBoxes([])
      return
    }
    const boxes: Array<TradeBox> = []
    // Finished trades keep theirs: entry to exit, with the SL / TP they last had.
    if (this.instrumentId !== null) {
      const currency = this.spec?.currency ?? state.summary?.currency ?? "USD"
      const orders = [...state.orderHistory, ...state.openOrders]
      for (const trade of closedTrades(state.fills, orders, this.instrumentId)) {
        const from = barTimeFor(this.bars, trade.openedAt)
        const to = barTimeFor(this.bars, trade.closedAt)
        if (from === undefined || to === undefined) continue
        const farthest = trade.takeProfits.at(-1)?.price ?? null
        const levels = trade.stopLoss !== null || farthest !== null
        const won = (trade.exit - trade.entry) * (trade.long ? 1 : -1) > 0
        boxes.push({
          from: from as UTCTimestamp,
          to: to as UTCTimestamp,
          entry: trade.entry,
          exit: trade.exit,
          long: trade.long,
          reward: levels ? farthest : won ? trade.exit : null,
          risk: levels ? trade.stopLoss : won ? null : trade.exit,
          targets: trade.takeProfits.slice(0, -1).map((target) => target.price),
          labels: boxLabels({
            entry: trade.entry,
            stopLoss: trade.stopLoss,
            takeProfits: trade.takeProfits,
            qty: trade.qty,
            pnl: trade.pnl,
            pnlName: "Closed P&L"
          }, this.spec?.tickSize ?? null, currency)
        })
      }
    }
    for (const box of positionBoxes(lines)) {
      const position = positionFor(state, this.instrumentId, box.product)
      const opened = position === undefined ? null : positionOpenedAt(state.fills, position)
      // Opened before the loaded fills or bars: from the first bar shown.
      const from = opened === null ? first.time : (barTimeFor(this.bars, opened) ?? first.time)
      boxes.push({
        from: from as UTCTimestamp,
        to: last.time as UTCTimestamp,
        extend: BOX_AHEAD,
        entry: toNumber(box.entry),
        exit: position?.mark != null ? toNumber(position.mark) : last.close,
        long: box.long,
        reward: box.takeProfits.length === 0 ? null : toNumber(box.takeProfits.at(-1)!),
        risk: box.stopLoss === null ? null : toNumber(box.stopLoss),
        targets: box.takeProfits.slice(0, -1).map(toNumber),
        ...(position === undefined || this.spec === null
          ? {}
          : { labels: positionBoxLabels(position, box, this.spec.tickSize, this.spec.qtyStep, this.spec.currency) })
      })
    }
    this.boxes.setBoxes(boxes)
  }

  private positionsKey(state: AccountState | null): string {
    if (state === null || this.instrumentId === null) return ""
    return state.positions
      .filter((position) => position.instrumentId === this.instrumentId)
      .map((position) => `${position.product}:${formatDecimal(position.unrealizedPnl)}`)
      .join(",")
  }

  private renderHandle(line: ChartLine, draft: Draft | undefined, state: AccountState | null): void {
    let handle = this.handles.get(line.key)
    if (handle === undefined) {
      handle = this.createHandle(line.key)
      this.handles.set(line.key, handle)
    }
    const { actions, label, root } = handle
    root.className = `trade-line trade-line-${line.tone}${draft !== undefined ? " trade-line-draft" : ""}`
    root.style.setProperty("--tone", COLORS[line.tone])
    label.disabled = !line.draggable
    label.title = line.draggable ? "Drag to move it; then Confirm" : ""
    label.textContent = this.labelText(line, state)

    actions.replaceChildren()
    if (draft !== undefined && draft.ledBy !== null) return
    if (draft !== undefined) {
      if (draft.error !== null) {
        const error = document.createElement("span")
        error.className = "trade-line-error"
        error.textContent = draft.error
        actions.append(error)
      }
      actions.append(
        button("Discard", "trade-line-button", "Put it back", () => this.discard(line.key)),
        button(draft.sending ? "…" : "Confirm", "trade-line-button trade-line-confirm", "Send the change", () => this.confirm(line.key))
      )
      return
    }
    if ((line.kind === "position" || line.kind === "order") && line.canAdd !== null) {
      if (line.canAdd.stopLoss) actions.append(this.legButton(line, "stopLoss"))
      if (line.canAdd.takeProfit) actions.append(this.legButton(line, "takeProfit"))
    }
    if (line.closable) {
      const title = line.kind === "position" ? "Close the position at market" : line.kind === "order" ? "Cancel the order" : "Remove it"
      label.title = line.kind === "order" && line.draggable ? "Drag to move it; hold Shift to move its stop-loss and take-profit too" : label.title
      actions.append(button("×", "trade-line-button trade-line-close", title, () => this.close(line)))
    }
  }

  private labelText(line: ChartLine, state: AccountState | null): string {
    const currency = this.spec?.currency ?? state?.summary?.currency ?? "USD"
    const position = positionFor(state, this.instrumentId, line.product)
    switch (line.kind) {
      case "position":
        return position === undefined ? line.label : `${line.label} · ${formatSignedMoney(position.unrealizedPnl, currency)}`
      case "stopLoss":
      case "takeProfit": {
        const parent = orderById(state, line.parentOrderId)
        const pnl = parent !== undefined
          ? orderPnlAt(parent, line.price)
          : position === undefined
          ? null
          : pnlAt(position, line.price, line.qty ?? undefined)
        // One of several take-profits: the part it closes, and that part's P&L (docs/design/0017).
        const part = line.qty === null ? "" : ` · ${formatDecimal(line.qty)}`
        return `${line.label} ${formatDecimal(line.price)}${part}${pnl === null ? "" : ` · ${formatSignedMoney(pnl, currency)}`}`
      }
      case "order":
        return `${line.label} @ ${formatDecimal(line.price)}`
    }
  }

  private createHandle(key: string): Handle {
    const root = document.createElement("div")
    const label = document.createElement("button")
    label.type = "button"
    label.className = "trade-line-label"
    const actions = document.createElement("span")
    actions.className = "trade-line-actions"
    root.append(label, actions)
    label.addEventListener("pointerdown", (event) => this.startDrag(key, label, event))
    this.overlay.append(root)
    return { root, label, actions }
  }

  /** Puts each label on its line (on a frame, as the chart redraws). */
  private schedulePlace(): void {
    if (this.placeFrame !== 0) return
    this.placeFrame = requestAnimationFrame(() => {
      this.placeFrame = 0
      this.place()
    })
  }

  private place(): void {
    const height = this.overlay.clientHeight
    const visible: Array<{ readonly handle: Handle; readonly y: number }> = []
    for (const [key, handle] of this.handles) {
      const line = this.shown.get(key)
      const y = line === undefined ? null : this.candles.priceToCoordinate(toNumber(line.price))
      if (y === null || y < 0 || y > height) {
        handle.root.hidden = true
        continue
      }
      handle.root.hidden = false
      visible.push({ handle, y: Math.round(y) })
    }
    // Labels of lines close in price would cover each other: a later one moves right of the ones it meets.
    visible.sort((a, b) => a.y - b.y)
    const placed: Array<{ readonly y: number; readonly x: number; readonly right: number }> = []
    // The chart's top-left holds the Buy / Sell buttons and the legends: a label beside them starts to their right.
    const buttons = this.overlay.parentElement?.parentElement?.querySelector<HTMLElement>(".pane-corner") ?? null
    const area = this.overlay.getBoundingClientRect()
    const reserved = buttons === null ? null : buttons.getBoundingClientRect()
    for (const { handle, y } of visible) {
      const width = handle.root.offsetWidth
      const besideButtons = reserved !== null && y + LABEL_HEIGHT / 2 > reserved.top - area.top && y - LABEL_HEIGHT / 2 < reserved.bottom - area.top
      let x = besideButtons ? reserved.right - area.left - 8 + LABEL_GAP : 0
      for (let moved = true; moved;) {
        moved = false
        for (const other of placed) {
          if (Math.abs(other.y - y) < LABEL_HEIGHT && x < other.right && x + width > other.x) {
            x = other.right + LABEL_GAP
            moved = true
          }
        }
      }
      placed.push({ y, x, right: x + width })
      handle.root.style.transform = `translate(${x}px, ${y}px)`
    }
  }

  // --- moving, confirming, closing --------------------------------------------------------------

  private startDrag(key: string, label: HTMLButtonElement, event: PointerEvent): void {
    const line = this.shown.get(key)
    if (line === undefined || !line.draggable || this.spec === null || event.button !== 0) return
    event.preventDefault()
    event.stopPropagation()
    const tick = this.spec.tickSize
    const original = this.drafts.get(key)?.line ?? line
    const state = accountStore.getState()
    const order = orderById(state, original.orderId) ?? orderById(state, original.parentOrderId)
    const leg = original.kind === "stopLoss" || original.kind === "takeProfit" ? original.kind : null
    // What Confirm sends: a pending order's own SL / TP, a Shift-drag of an order with its brackets, or a plain move.
    const shifted = event.shiftKey && original.kind === "order" && order?.protection != null && original.field !== null
    const request = (at: Decimal): LineRequest | null => {
      const latest = orderById(accountStore.getState(), order?.id ?? null) ?? order
      if (original.parentOrderId !== null && leg !== null) return latest === undefined ? null : orderProtectRequest(latest, leg, at)
      if (shifted && latest !== undefined && original.field !== null) return shiftRequest(latest, original.field, at)
      return moveRequest(original, at)
    }
    // A Shift-drag moves the order's SL / TP lines by the same amount, without buttons of their own.
    const followers = shifted && order !== undefined
      ? (["stopLoss", "takeProfit"] as const).flatMap((name) => {
        const followed = this.shown.get(`${order.id}:${name}`)
        return followed === undefined ? [] : [followed]
      })
      : []
    label.setPointerCapture(event.pointerId)
    const move = (moved: PointerEvent) => {
      const top = this.overlay.getBoundingClientRect().top
      const raw = this.candles.coordinateToPrice(moved.clientY - top)
      const price = raw === null ? null : snapPrice(raw, tick)
      if (price === null) return
      const draft = this.drafts.get(key)
      if (draft !== undefined) {
        draft.price = price
        draft.error = null
      } else {
        this.drafts.set(key, { line: original, price, request, sending: false, error: null, sentAt: null, ledBy: null })
      }
      if (followers.length > 0) {
        const offset = BigDecimal.subtract(price, original.price)
        for (const follower of followers) {
          const at = BigDecimal.sum(follower.price, offset)
          this.drafts.set(follower.key, { line: follower, price: at, request: () => null, sending: false, error: null, sentAt: null, ledBy: key })
        }
      }
      this.draw()
    }
    const end = () => {
      label.removeEventListener("pointermove", move)
      label.removeEventListener("pointerup", end)
      label.removeEventListener("pointercancel", end)
      const draft = this.drafts.get(key)
      // Dropped where it was: nothing to confirm.
      if (draft !== undefined && !original.key.startsWith("new:") && BigDecimal.equals(draft.price, original.price)) this.discard(key)
      this.draw()
    }
    label.addEventListener("pointermove", move)
    label.addEventListener("pointerup", end)
    label.addEventListener("pointercancel", end)
  }

  /** A new stop-loss or take-profit line, 1% from the position or the working order, ready to drag and confirm. */
  /**
   * SL / TP on a position or a working order, as on TradingView: press and drag it to the price
   * (then Confirm), or click it for a line 1% away. The pointer is followed on the window: the
   * button itself is redrawn while the new line moves.
   */
  private legButton(line: ChartLine, leg: "stopLoss" | "takeProfit"): HTMLButtonElement {
    // A position's second to fourth take-profit is TP2 … TP4 (docs/design/0017).
    const number = line.canAdd?.takeProfitNumber ?? 1
    const name = leg === "stopLoss" ? "SL" : number > 1 ? `TP${number}` : "TP"
    const element = document.createElement("button")
    element.type = "button"
    element.className = "trade-line-button trade-line-leg"
    element.textContent = name
    element.title = leg === "stopLoss"
      ? "Drag to place a stop-loss, or click for one 1% away"
      : number > 1
      ? `Add take-profit ${number}: the position is split equally between the targets. Drag to place it, or click for the next 1R`
      : "Drag to place a take-profit, or click for one at 1R (1% away without a stop-loss)"
    element.addEventListener("click", (event) => event.stopPropagation())
    element.addEventListener("pointerdown", (down) => {
      if (down.button !== 0 || this.spec === null) return
      down.preventDefault()
      down.stopPropagation()
      const tick = this.spec.tickSize
      const startY = down.clientY
      let key: string | null = null
      const move = (moved: PointerEvent) => {
        if (key === null && Math.abs(moved.clientY - startY) < 4) return
        key ??= this.addLeg(line, leg)
        const draft = key === null ? undefined : this.drafts.get(key)
        const raw = this.candles.coordinateToPrice(moved.clientY - this.overlay.getBoundingClientRect().top)
        const price = raw === null ? null : snapPrice(raw, tick)
        if (draft === undefined || price === null) return
        draft.price = price
        draft.error = null
        this.draw()
      }
      const end = () => {
        window.removeEventListener("pointermove", move)
        window.removeEventListener("pointerup", end)
        window.removeEventListener("pointercancel", end)
        // A click (no drag): the line 1% away, to drag or confirm.
        if (key === null) this.addLeg(line, leg)
      }
      window.addEventListener("pointermove", move)
      window.addEventListener("pointerup", end)
      window.addEventListener("pointercancel", end)
    })
    return element
  }

  /** Adds the draft line and returns its key; null when there's nothing to place it on. */
  private addLeg(line: ChartLine, leg: "stopLoss" | "takeProfit"): string | null {
    const state = accountStore.getState()
    if (this.spec === null) return null
    const order = line.kind === "order" ? orderById(state, line.orderId) : undefined
    const position = line.kind === "position" ? positionFor(state, this.instrumentId, line.product) : undefined
    const price = order !== undefined
      ? orderStartingPrice(order, leg, this.spec.tickSize)
      : position !== undefined
      ? startingPrice(position, leg, this.spec.tickSize)
      : null
    if (price === null) return null
    const draftLine: ChartLine = {
      // A working order's new leg takes the key its line will have, so the update replaces the draft.
      key: order !== undefined ? `${order.id}:${leg}` : `new:${leg}:${line.product}`,
      kind: leg,
      price,
      label: leg === "stopLoss" ? "SL" : "TP",
      tone: leg === "stopLoss" ? "loss" : "profit",
      draggable: true,
      closable: false,
      orderId: null,
      product: line.product,
      field: null,
      canAdd: null,
      parentOrderId: order?.id ?? null,
      qty: null
    }
    const request = (at: Decimal): LineRequest | null => {
      if (order !== undefined) {
        const latest = orderById(accountStore.getState(), order.id)
        return latest === undefined ? null : orderProtectRequest(latest, leg, at)
      }
      return position === undefined ? null : protectRequest(position, leg, at)
    }
    this.drafts.set(draftLine.key, { line: draftLine, price, request, sending: false, error: null, sentAt: null, ledBy: null })
    this.draw()
    return draftLine.key
  }

  private discard(key: string): void {
    this.drafts.delete(key)
    for (const [other, draft] of this.drafts) if (draft.ledBy === key) this.drafts.delete(other)
    this.draw()
  }

  private confirm(key: string): void {
    const draft = this.drafts.get(key)
    const accountId = accountStore.getState()?.accountId
    if (draft === undefined || draft.sending || accountId === undefined) return
    const request = draft.request(draft.price)
    if (request === null) return
    draft.sending = true
    draft.error = null
    this.draw()
    sendLineRequest(accountId, request).then(
      () => {
        draft.sending = false
        draft.sentAt = Date.now()
        for (const follower of this.drafts.values()) if (follower.ledBy === key) follower.sentAt = draft.sentAt
        // A position's new SL / TP comes back as its own line (its order's key): this draft is done.
        if (key.startsWith("new:")) this.drafts.delete(key)
        this.draw()
        setTimeout(() => this.draw(), 3100)
      },
      (error: unknown) => {
        draft.sending = false
        const message = messageFor(error)
        draft.error = message.detail || message.title
        this.draw()
      }
    )
  }

  private close(line: ChartLine): void {
    const state = accountStore.getState()
    const accountId = state?.accountId
    if (accountId === undefined) return
    const failed = (what: string) => (error: unknown) => {
      const message = messageFor(error)
      toast("error", `Couldn't ${what}: ${message.title}`, message.detail)
    }
    const position = positionFor(state, this.instrumentId, line.product)
    switch (line.kind) {
      case "order":
        if (line.orderId !== null) tradingApi.cancelOrder(accountId, line.orderId).catch(failed("cancel the order"))
        return
      case "stopLoss":
      case "takeProfit": {
        const parent = orderById(state, line.parentOrderId)
        if (parent !== undefined) {
          sendLineRequest(accountId, orderProtectRequest(parent, line.kind, null)).catch(failed(`remove the ${line.kind === "stopLoss" ? "stop-loss" : "take-profit"}`))
          return
        }
        if (position !== undefined) {
          // A take-profit of several: only that one goes; the others re-split the position.
          sendLineRequest(accountId, protectRequest(position, line.kind, null, line.orderId)).catch(
            failed(`remove the ${line.kind === "stopLoss" ? "stop-loss" : "take-profit"}`)
          )
        }
        return
      }
      case "position":
        if (position !== undefined) closePosition(accountId, position).catch(failed("close the position"))
        return
    }
  }

  // --- fills --------------------------------------------------------------------------------------

  private drawMarkers(state: AccountState | null): void {
    const fills = state === null || this.instrumentId === null ? [] : fillsOn(state, this.instrumentId)
    const first = this.bars[0]?.time ?? 0
    const last = this.bars.at(-1)?.time ?? 0
    const key = `${this.instrumentId}|${fills.length}|${fills[0]?.id ?? ""}|${this.bars.length}|${first}|${last}`
    if (key === this.markerKey) return
    this.markerKey = key
    const markers: Array<SeriesMarker<Time>> = []
    const tips = new Map<string, string>()
    const currency = this.spec?.currency ?? state?.summary?.currency ?? "USD"
    for (const fill of fills) {
      const time = barTimeFor(this.bars, fill.time)
      if (time === undefined) continue
      const buy = fill.side === "Buy"
      const id = `fill-${fill.id}`
      tips.set(
        id,
        [
          `${buy ? "Buy" : "Sell"} ${formatDecimal(fill.qty)} @ ${formatDecimal(fill.price)}`,
          formatTime(fill.time),
          BigDecimal.isZero(fill.realizedPnl) ? null : `Realized ${formatSignedMoney(fill.realizedPnl, currency)}`
        ].filter((line) => line !== null).join("\n")
      )
      markers.push({
        id,
        time: time as UTCTimestamp,
        position: buy ? "belowBar" : "aboveBar",
        shape: buy ? "arrowUp" : "arrowDown",
        color: buy ? BUY : SELL,
        text: `${buy ? "B" : "S"} ${formatDecimal(fill.qty)}`
      })
    }
    markers.sort((a, b) => (a.time as number) - (b.time as number))
    this.markers.setMarkers(markers)
    this.tips.set(tips)
  }
}
